import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import type { AgentToken, AgentTokenMintResponse } from '../api/types';

// Admin page for create-only agent API tokens (issue #13). Mint a named
// token (shown exactly once), list existing tokens (never the literal),
// and revoke (icon button, effective immediately).
export default function AgentTokensPage() {
  const [tokens, setTokens] = useState<AgentToken[]>([]);
  const [label, setLabel] = useState('');
  const [minting, setMinting] = useState(false);
  const [error, setError] = useState('');
  const [minted, setMinted] = useState<AgentTokenMintResponse | null>(null);
  const [copied, setCopied] = useState(false);

  async function load() {
    try {
      const r = await api<AgentToken[]>('/admin/agent-tokens');
      setTokens(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function copyToken(token: string) {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be unavailable on http://; the .token div is
      // user-select: all so the operator can select + copy manually.
    }
  }

  async function mint(e: FormEvent) {
    e.preventDefault();
    setError('');
    setMinted(null);
    const trimmed = label.trim();
    if (!trimmed) {
      setError('label is required');
      return;
    }
    setMinting(true);
    try {
      const r = await api<AgentTokenMintResponse>('/admin/agent-tokens', {
        method: 'POST',
        body: JSON.stringify({ label: trimmed }),
      });
      setMinted(r);
      setLabel('');
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'mint failed');
    } finally {
      setMinting(false);
    }
  }

  async function revoke(id: string, tokenLabel: string) {
    if (!confirm(`revoke agent token "${tokenLabel}" (${id})? This is immediate.`)) return;
    setError('');
    try {
      await api(`/admin/agent-tokens/${encodeURIComponent(id)}`, { method: 'DELETE' });
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'revoke failed');
    }
  }

  return (
    <>
      <div className="card">
        <h1>agent tokens</h1>
        <p className="muted" style={{ marginBottom: 16 }}>
          Create-only API tokens for agents. An agent sends{' '}
          <code>Authorization: Bearer drop_…</code> to <code>POST /api/secrets</code>{' '}
          — it can create drops but cannot retrieve, list, or administer. The token
          value is shown once at mint time and stored only as a hash.
        </p>
        {error && <div className="error-banner">{error}</div>}
        {minted && (
          <div className="success-banner" style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8 }}>
              token minted for <strong>{minted.label}</strong> — copy it now, it is
              shown only once:
            </div>
            <div className="token">{minted.token}</div>
            <div className="copy-row">
              <button
                type="button"
                className="secondary"
                onClick={() => copyToken(minted.token)}
              >
                {copied ? 'copied!' : 'copy token'}
              </button>
            </div>
            <div style={{ marginTop: 12 }} className="muted">
              id={minted.id} · source={minted.source}
            </div>
          </div>
        )}
        <form onSubmit={mint}>
          <label className="label">Label (e.g. agent hostname)</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="agent-host-01"
            maxLength={200}
            required
          />
          <div className="actions">
            <button type="submit" disabled={minting || !label.trim()}>
              {minting ? 'minting…' : 'mint token'}
            </button>
          </div>
        </form>
      </div>

      <div className="card">
        <h2>existing tokens</h2>
        {tokens.length === 0 ? (
          <p className="muted">no agent tokens</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>id</th>
                <th>label</th>
                <th>source</th>
                <th>created</th>
                <th>last used</th>
                <th>status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => {
                const revoked = t.revoked_at != null;
                return (
                  <tr key={t.id} style={revoked ? { opacity: 0.5 } : undefined}>
                    <td>
                      <code>{t.id}</code>
                    </td>
                    <td>{t.label}</td>
                    <td className="muted">{t.source}</td>
                    <td className="muted">{fmtDate(t.created_at)}</td>
                    <td className="muted">
                      {t.last_used_at != null ? fmtDate(t.last_used_at) : '—'}
                    </td>
                    <td>{revoked ? 'revoked' : 'active'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {!revoked && (
                        <button
                          className="icon-button"
                          title="revoke token"
                          aria-label={`revoke token ${t.label}`}
                          onClick={() => revoke(t.id, t.label)}
                        >
                          <TrashIcon />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function fmtDate(ts: number): string {
  return new Date(ts * 1000).toLocaleString();
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 6h18" strokeLinecap="round" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" strokeLinecap="round" />
      <path
        d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 11v6M14 11v6" strokeLinecap="round" />
    </svg>
  );
}
